import { useRef, useState } from 'react';
import { Dialog, Popover } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { PriceAmountWithDetails } from '@/api';
import { PriceWithDetailsTooltipWithConversions } from '@/components/PriceWithDetails/PriceWithDetails';
import WorkshopPriceLabel from '@/components/WorkshopPriceLabel/WorkshopPriceLabel';
import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import { InputPercentage } from '@/design_system/InputNumber';
import PhotoCard from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconEdit from '@/icons/Edit.svg';
import IconWarning from '@/icons/Warning.svg';
import { getRefashionStatus } from '@/models/actionType';
import {
  getArticlePhoto,
  useArticleName,
  useGetArticleName,
  useUpdateArticle,
} from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import { formatCurrency } from '@/utils/number';
import { shouldShowPendingOrganizationQuote, shouldShowPendingWorkshopQuote } from '@/utils/price';
import useViewPort from '@/utils/useViewport';

import { Threshold } from './components/Threshold/Threshold';

import './ArticlePrice.css';

export const ArticlePrice = ({
  allowDiscount,
  hideArticleName,
}: {
  allowDiscount: boolean;
  hideArticleName?: boolean;
}) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();
  const { article, request } = useArticleContext();

  const { isMobile } = useViewPort();
  const getArticleName = useGetArticleName();

  const name = useArticleName({ article });

  if (!workshopPrice.shown && !organizationPrice.shown) {
    return null;
  }

  const _hideArticleName = isMobile || hideArticleName;
  const _hidePrice = !!request.allArticles.find((art) => art.exchangedFromId === article.id);

  if (_hidePrice && _hideArticleName) {
    return null;
  }

  const exchangedFromArticle = request?.allArticles.find(
    (articleExchangedFrom) => articleExchangedFrom.id === article.exchangedFromId
  );

  return (
    <Stack gap=".5rem" ariaLabel={t({ id: 'article.header.price.label', message: 'Total price' })}>
      {!_hideArticleName && (
        <Stack row gap="16px" alignItems="center">
          <Title as="h1" maxLines={2} title={name}>
            {name}
          </Title>
          {!!exchangedFromArticle && (
            <Box padding="8px">
              <Stack row gap="0.5rem" alignItems="center">
                {!!getArticlePhoto(exchangedFromArticle) && !isMobile && (
                  <PhotoCard
                    url={getArticlePhoto(exchangedFromArticle)!.url}
                    name={t({ id: 'article.information.article-photo', message: 'Item photo' })}
                    alt={t({ id: 'article.information.article-photo', message: 'Item photo' })}
                    size="small"
                  />
                )}
                <Stack>
                  <InlineText size="200" color="secondary">
                    <Trans id="article.price.exchangedFrom">Replacement item for</Trans>
                  </InlineText>
                  <Button
                    variant="link"
                    size="compact"
                    to={`/requests/${request.id}/articles/${exchangedFromArticle.id}`}
                  >
                    <Paragraph size="200" color="secondary">
                      {getArticleName({ article: exchangedFromArticle })}
                    </Paragraph>
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {!_hidePrice && <PricesBox allowDiscount={allowDiscount} />}
    </Stack>
  );
};

const PricesBox = ({ allowDiscount }: { allowDiscount: boolean }) => {
  const { article, view: articleView, iam } = useArticleContext();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();
  const { isMobile } = useViewPort();

  return isMobile ? (
    <>
      {workshopPrice.shown && (
        <Stack gap="0.25rem">
          <WorkshopPrice
            price={article.snapshot.workshopPrice?.amountPerEntity?.find(
              (amount) => amount.entityId === article.workshopId
            )}
            label={workshopPrice.label}
            showAmountBeforeTaxes={workshopPrice.showAmountBeforeTaxes}
          />
          {!iam.hasOnlyWorkshopScopeOnArticle &&
            articleView.prices.thresholds.organization.shown && <Threshold type="organization" />}
        </Stack>
      )}

      {organizationPrice.shown && (
        <Stack gap="0.25rem">
          <OrganizationPrice
            price={article.snapshot.organizationPrice?.amountPerCurrency?.[0]}
            label={organizationPrice.label}
            allowDiscount={allowDiscount}
            showAmountBeforeTaxes={organizationPrice.showAmountBeforeTaxes}
          />
          {articleView.prices.thresholds.client.shown && <Threshold type="client" />}
        </Stack>
      )}
    </>
  ) : (
    <Stack
      gap={'0.75rem'}
      alignItems="stretch"
      justifyContent={!isMobile ? 'space-between' : 'flex-start'}
      flexWrap="nowrap"
    >
      {workshopPrice.shown && (
        <Stack gap="0.25rem">
          <WorkshopPrice
            price={article.snapshot.workshopPrice?.amountPerEntity?.find(
              (amount) => amount.entityId === article.workshopId
            )}
            label={workshopPrice.label}
            showAmountBeforeTaxes={workshopPrice.showAmountBeforeTaxes}
          />
          {!iam.hasOnlyWorkshopScopeOnArticle &&
            articleView.prices.thresholds.organization.shown && <Threshold type="organization" />}
        </Stack>
      )}

      {organizationPrice.shown && (
        <Stack gap="0.25rem">
          <OrganizationPrice
            price={article.snapshot.organizationPrice?.amountPerCurrency?.[0]}
            label={organizationPrice.label}
            allowDiscount={allowDiscount}
            showAmountBeforeTaxes={organizationPrice.showAmountBeforeTaxes}
          />
          {articleView.prices.thresholds.client.shown && <Threshold type="client" />}
        </Stack>
      )}
    </Stack>
  );
};

const WorkshopPrice = ({
  price,
  label,
  showAmountBeforeTaxes,
}: {
  price: PriceAmountWithDetails | undefined;
  label: string;
  showAmountBeforeTaxes: boolean;
}) => {
  const { isMobile } = useViewPort();
  const { article } = useArticleContext();

  return (
    <Stack
      ariaLabel={label}
      row={isMobile}
      gap={isMobile ? undefined : '0.125rem'}
      alignItems={isMobile ? 'center' : 'flex-start'}
      justifyContent={isMobile ? 'space-between' : undefined}
    >
      <WorkshopPriceLabel
        noVATApplied={price?.noVATApplied}
        size={isMobile ? '100' : '200'}
        color="secondary"
      />
      <Price
        price={price}
        showAmountBeforeTaxes={showAmountBeforeTaxes}
        emptyFallback={shouldShowPendingWorkshopQuote(article) ? 'pending-quote' : 'dash'}
      />
    </Stack>
  );
};

const OrganizationPrice = ({
  price,
  label,
  allowDiscount,
  showAmountBeforeTaxes,
}: {
  price: PriceAmountWithDetails | undefined;
  label: string;
  allowDiscount: boolean;
  showAmountBeforeTaxes: boolean;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const { article, request } = useArticleContext();

  const [isPopoverOpened, setIsPopoverOpened] = useState(false);
  const popoverAnchorRef = useRef<HTMLDivElement>(null);

  const showWarning =
    !request.vip &&
    !!article.currentPriceManualDiscount &&
    article.snapshot.articleActions.some(
      (action) => getRefashionStatus(action.organizationPrice) === 'not-applied-discount'
    );

  return (
    <Stack
      ariaLabel={label}
      gap={isMobile ? undefined : '0.125rem'}
      row={isMobile}
      alignItems={isMobile ? 'center' : 'flex-start'}
      justifyContent={isMobile ? 'space-between' : undefined}
    >
      <InlineText size={isMobile ? '100' : '200'} color="secondary">
        {label}
      </InlineText>
      <Stack row gap="0.25rem" alignItems="center" ref={popoverAnchorRef}>
        {allowDiscount && showWarning && (
          <Tooltip
            content={t({
              id: 'article.header.price.edit.warning',
              message:
                'This discount conflicts with the repair bonus, preventing it from applying to some actions.',
            })}
            placement="bottom"
          >
            <Button variant="style-less">
              <IconWarning className="text-warning" style={{ fontSize: '1.125rem' }} />
            </Button>
          </Tooltip>
        )}
        <Stack row gap="0.5rem">
          <Price
            price={price}
            showAmountBeforeTaxes={showAmountBeforeTaxes}
            emptyFallback={shouldShowPendingOrganizationQuote(article) ? 'pending-quote' : 'dash'}
          />
          {allowDiscount && (
            <>
              {!isPopoverOpened && (
                <Button
                  size="small"
                  variant="secondary"
                  iconOnly
                  ariaLabel={t({ id: 'article.header.price.edit.trigger', message: 'Edit price' })}
                  tooltip={t({
                    id: 'article.header.price.apply-discount.tooltip',
                    message: 'Apply a discount',
                  })}
                  onPress={() => setIsPopoverOpened(true)}
                >
                  <IconEdit />
                </Button>
              )}
              <Popover
                placement="bottom left"
                triggerRef={popoverAnchorRef}
                isOpen={isPopoverOpened}
                onOpenChange={setIsPopoverOpened}
                offset={4}
              >
                <EditOrganizationPricePopover article={article} showWarning={showWarning} />
              </Popover>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const Price = ({
  price,
  showAmountBeforeTaxes,
  emptyFallback,
}: {
  price: PriceAmountWithDetails | undefined;
  showAmountBeforeTaxes: boolean;
  emptyFallback: 'dash' | 'pending-quote';
}) => {
  const typographyClass = 'paragraph-100-medium';

  if (!price) {
    return (
      <span className={typographyClass}>
        {emptyFallback === 'pending-quote' ? (
          <Trans id="article.price.pending-quote">Pending quote</Trans>
        ) : (
          '-'
        )}
      </span>
    );
  }

  if (price.cancelled) {
    return (
      <s className={`${typographyClass} text-disabled`}>
        {formatCurrency(price.amount, price.currency)}
      </s>
    );
  }

  return (
    <PriceWithDetailsTooltipWithConversions
      price={price}
      className={typographyClass}
      options={{ showAmountBeforeTaxes }}
      tooltipDirection="row-reverse"
    />
  );
};

const { block } = createBEMClasses('edit-article-price-popover');

const EditOrganizationPricePopover = ({
  article,
  showWarning,
}: {
  article: ArticleWithRelations;
  showWarning: boolean;
}) => {
  const { t } = useLingui();
  const [priceManualDiscount, setPriceManualDiscount] = useState(
    article.currentPriceManualDiscount ? -article.currentPriceManualDiscount : null
  );

  const {
    mutateAsync: updateArticle,
    isPending: isUpdatingArticle,
    isError,
  } = useUpdateArticle({
    articleId: article.id,
    requestId: article.requestId,
  });

  return (
    <Dialog
      className={block()}
      aria-label={t({ id: 'article.header.price.edit', message: 'Edit price' })}
    >
      <Stack gap="1rem">
        <Paragraph color="secondary">
          <Trans id="article.header.price.edit.content">
            Apply a discount percentage. This will apply if you add new actions.
          </Trans>
        </Paragraph>
        <InputPercentage
          ariaLabel={t({ id: 'article.header.price.edit.label', message: 'Price discount' })}
          size="small"
          placeholder={t({ id: 'article.header.price.edit.placeholder', message: 'i.e: - 10%' })}
          isLoading={isUpdatingArticle}
          value={priceManualDiscount ?? undefined}
          minValue={-1}
          maxValue={1}
          onChange={(value) => {
            const negativeValue = -Math.abs(value);

            setPriceManualDiscount(negativeValue);

            if (value !== article.currentPriceManualDiscount) {
              updateArticle({
                data: { priceManualDiscount: isNaN(value) ? null : Math.abs(value) },
              });
            }
          }}
          error={
            isError ? t({ id: '_general.error.unknown', message: 'Unknown error' }) : undefined
          }
          round
        />
        {showWarning && (
          <AlertBar type="warning" size="large">
            <Trans id="article.header.price.edit.warning">
              This discount conflicts with the repair bonus, preventing it from applying to some
              actions.
            </Trans>
          </AlertBar>
        )}
      </Stack>
    </Dialog>
  );
};
